import {
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import type { LimitViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useTranslation } from 'react-i18next';

import { LIMIT_REFERENCES_QUERY } from './limits.gql.tsx';

type LimitReferencesData = Pick<LimitViewModel, 'limitId' | 'pricingPolicies'>;

interface LimitReferencesTabProps {
	limitId: string;
}

export function LimitReferencesTab({ limitId }: LimitReferencesTabProps) {
	const { t } = useTranslation('common');
	const { t: tL } = useTranslation('limits');

	const { data, isLoading, isError } = useOne<LimitReferencesData>({
		resource: 'limits',
		id: limitId,
		meta: {
			gqlQuery: LIMIT_REFERENCES_QUERY,
			dataPath: 'limit'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100,
		showInitialLoading: true
	});

	const limit = data?.data;

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	if (!isLoading && (isError || !limit)) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tL('details.loadError')}
			</div>
		);
	}

	if (!limit) {
		return null;
	}

	const pricingPolicies = limit.pricingPolicies ?? [];

	return (
		<div className="space-y-6">
			<DetailSection title={t('sections.pricing_policies')}>
				{pricingPolicies.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4">
						{tL('details.noPolicies')}
					</p>
				) : (
					<DetailFieldsGrid>
						{pricingPolicies.map(policy => (
							<EntityDetailField
								key={policy.pricingPolicyId}
								label={tL('details.policy')}
								value={policy.name}
								entityId={policy.pricingPolicyId}
								entityType="pricingPolicy"
							/>
						))}
					</DetailFieldsGrid>
				)}
			</DetailSection>
		</div>
	);
}
